import React from 'react';

import type { CalendarEventResponse } from '@troia-v3/types';

import { Icon } from '@/shared/icons';

import { formatDateTime } from '../detailsFormat';
import type { DetailsData } from '../useDetailsData';

interface FollowUpsLayerProps {
  data: DetailsData;
  /** Abre o cadastro de um retorno novo. */
  onNew: () => void;
  /** Abre o retorno clicado. Quem monta a camada decide o que fazer com ele. */
  onOpen: (followUp: CalendarEventResponse) => void;
}

/**
 * Cartão de um retorno. Não existe classe `.cdp-*` para ele: as peças prontas
 * (`.cdp-line`, `.cdp-kv`) são linhas de ficha, sem moldura.
 */
const CARTAO: React.CSSProperties = {
  border: '1px solid var(--theme-sidebar-border)',
  borderRadius: 8,
  padding: 10,
  marginBottom: 6,
};

/**
 * O primeiro retorno por vir é o motivo de a camada existir — ganha a tinta da
 * cor primária, como no desenho aprovado. `color-mix` sobre tokens que invertem
 * com o tema é o mesmo caminho do resto do painel: funciona no claro e no
 * escuro sem cor fixa.
 */
const CARTAO_PROXIMO: React.CSSProperties = {
  ...CARTAO,
  background: 'color-mix(in srgb, var(--theme-primary) 9%, var(--theme-sidebar-background))',
  border: '1px solid color-mix(in srgb, var(--theme-primary) 26%, transparent)',
};

const QUANDO: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.3,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--theme-card-text)',
};

const RESUMO: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.35,
  marginTop: 1,
  color: 'var(--theme-card-text)',
};

const DESCRICAO: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.35,
  color: 'var(--theme-muted-text)',
};

interface ItemProps {
  followUp: CalendarEventResponse;
  /** Posição na lista que a API devolveu — mantém o testid único nas duas seções. */
  posicao: number;
  destaque: boolean;
  onOpen: (followUp: CalendarEventResponse) => void;
}

const ItemDeRetorno: React.FC<ItemProps> = ({ followUp, posicao, destaque, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(followUp)}
    data-testid={`details-followup-${posicao}`}
    className="flex w-full items-start gap-2 text-left"
    style={destaque ? CARTAO_PROXIMO : CARTAO}
  >
    <span
      className="flex-none"
      style={{ color: destaque ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
    >
      {/* Um retorno é um compromisso com data marcada — o mesmo ícone serve
          para o que vem e para o que já passou. Só a cor muda: nada aqui diz
          que o retorno passado foi cumprido, e um "concluído" mentiria. */}
      <Icon name="calendar" className="w-4 h-4" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block" style={destaque ? { ...QUANDO, color: 'var(--theme-primary)' } : QUANDO}>
        {formatDateTime(followUp.startTime)}
      </span>
      <span className="block" style={RESUMO}>
        {followUp.summary}
      </span>
      {/* Sem `block` junto do `line-clamp-2`: a classe do corte já define o
          `display` da caixa, e as duas disputando a mesma propriedade deixariam
          o corte de duas linhas a cargo da ordem do CSS gerado. */}
      {followUp.description && (
        <span className="mt-1 line-clamp-2" style={DESCRICAO}>
          {followUp.description}
        </span>
      )}
    </span>
  </button>
);

/**
 * Camada Retornos: os retornos agendados do contato.
 *
 * A API devolve TUDO ordenado por data crescente — o mais ANTIGO primeiro, sem
 * cortar o que já passou (`findFollowUpsByContact`, `sort({ startTime: 1 })`).
 * Jogar essa lista na tela como veio coloca no topo a ligação de meses atrás e
 * empurra para o fim justamente a pergunta que traz alguém a esta camada:
 * "quando eu falo com essa pessoa de novo?". Por isso a divisão em "Próximos"
 * (o que vem, do mais perto ao mais distante) e "Já aconteceu" (o que passou,
 * do mais recente para trás) — a mesma do desenho aprovado.
 *
 * O botão "Novo retorno" fica FORA das três situações (carregando, vazio, com
 * lista): agendar é a razão de entrar aqui, e escondê-lo enquanto a busca não
 * responde deixaria a tela sem saída por alguns instantes.
 */
const FollowUpsLayer: React.FC<FollowUpsLayerProps> = ({ data, onNew, onOpen }) => {
  const { followUps, isLoadingFollowUps } = data;

  const agora = Date.now();
  const proximos: Array<{ followUp: CalendarEventResponse; posicao: number }> = [];
  const passados: Array<{ followUp: CalendarEventResponse; posicao: number }> = [];

  followUps.forEach((followUp, posicao) => {
    const quando = new Date(followUp.startTime).getTime();
    // Data ilegível conta como "por vir": esconder o retorno na parte de baixo
    // some com o defeito; deixá-lo em cima mostra o travessão e o problema.
    if (Number.isNaN(quando) || quando >= agora) {
      proximos.push({ followUp, posicao });
    } else {
      passados.push({ followUp, posicao });
    }
  });
  // A ordem da API é crescente; o passado se lê ao contrário, do mais recente.
  passados.reverse();

  return (
    <>
      <div data-testid="details-followups-list">
        {isLoadingFollowUps && (
          <p className="cdp-empty" data-testid="details-followups-loading">
            Carregando os retornos…
          </p>
        )}

        {!isLoadingFollowUps && proximos.length > 0 && (
          <>
            <div className="cdp-seclabel">Próximos</div>
            {proximos.map((item, ordem) => (
              <ItemDeRetorno
                key={item.followUp.id}
                followUp={item.followUp}
                posicao={item.posicao}
                // Só o primeiro é destaque: destacar todos não destaca nenhum.
                destaque={ordem === 0}
                onOpen={onOpen}
              />
            ))}
          </>
        )}

        {!isLoadingFollowUps && proximos.length === 0 && (
          <p className="cdp-empty" data-testid="details-followups-empty">
            Nenhum retorno agendado.
          </p>
        )}
      </div>

      <button type="button" className="cdp-btn" onClick={onNew} data-testid="details-new-followup">
        <Icon name="plus" className="w-4 h-4" />
        Novo retorno
      </button>

      {!isLoadingFollowUps && passados.length > 0 && (
        <>
          <div className="cdp-seclabel">Já aconteceu</div>
          {passados.map((item) => (
            <ItemDeRetorno
              key={item.followUp.id}
              followUp={item.followUp}
              posicao={item.posicao}
              destaque={false}
              onOpen={onOpen}
            />
          ))}
        </>
      )}
    </>
  );
};

export default FollowUpsLayer;
